import React, { useContext, useState } from "react";
import {
    Form, Input, Button, Select, message,
    Card, Switch, Tag, Divider, Typography
} from "antd";
import {
    LockOutlined, UnlockOutlined,
    AndroidOutlined, AppleOutlined
} from "@ant-design/icons";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const { Option } = Select;
const { Text } = Typography;

export default function VersionUpdate() {
    const [configForm] = Form.useForm();
    const [updateForm] = Form.useForm();
    const { authData, backUrl } = useContext(AuthContext);

    const [platform, setPlatform] = useState("android");
    const [isUnlocked, setIsUnlocked] = useState(false);

    const platformEndpoint = () =>
        platform === "ios"
            ? `${backUrl}/api/emp/ios/update-status/`
            : `${backUrl}/api/emp/android/update-status/`;

    const authHeader = () => ({
        Authorization: `Bearer ${authData?.access}`,
    });

    const handlePlatformChange = (checked) => {
        setPlatform(checked ? "ios" : "android");
        setIsUnlocked(false);
        updateForm.resetFields();
        configForm.resetFields();
    };

    const onUpdateFinish = async (values) => {
        try {
            const payload = {
                [`${platform}_latest_version`]: values.latest_version,
                [`${platform}_remarks`]: values.remarks,
            };
            await axios.post(platformEndpoint(), payload, {
                headers: authHeader(),
            });
            message.success(`Latest version saved for ${platform === "ios" ? "iOS" : "Android"}`);

            // Populate Card 2 only with the version just submitted
            configForm.resetFields();
            configForm.setFieldsValue({
                latest_version: values.latest_version,
            });

            setIsUnlocked(true);
            message.info("App Version Update card is now unlocked.");
        } catch (err) {
            message.error("Something went wrong while saving latest version");
        }
    };

    const onConfigFinish = async (values) => {
        try {
            const payload = {
                [`${platform}_latest_version`]: values.latest_version,
                [`${platform}_force_update_version`]: values.force_update_version,
                [`${platform}_update_type`]: values.update_type,
                [`${platform}_store_url`]: values.store_url,
                [`${platform}_version_remarks`]: values.version_remarks,
            };
            await axios.post(platformEndpoint(), payload, {
                headers: authHeader(),
            });
            message.success("App version update triggered successfully");

            // Clear both forms and re-lock
            updateForm.resetFields();
            configForm.resetFields();
            setIsUnlocked(false);
        } catch (err) {
            message.error("Something went wrong while triggering update");
        }
    };

    const cardStyle = { maxWidth: 620, margin: "0 auto 32px" };

    const platformTag = platform === "android"
        ? <Tag icon={<AndroidOutlined />} color="success">Android</Tag>
        : <Tag icon={<AppleOutlined />} color="default">iOS</Tag>;

    const platformSwitch = (
        <Switch
            checkedChildren={<><AppleOutlined /> iOS</>}
            unCheckedChildren={<><AndroidOutlined /> Android</>}
            checked={platform === "ios"}
            onChange={handlePlatformChange}
            style={{ backgroundColor: platform === "ios" ? "#555" : "#3ddc84" }}
        />
    );

    return (
        <div style={{ padding: "24px" }}>

            <Card title="Latest Update Version" style={cardStyle} extra={platformSwitch}>
                <div style={{ marginBottom: 16 }}>{platformTag}</div>

                <Form form={updateForm} layout="vertical" onFinish={onUpdateFinish}>
                    <Form.Item
                        name="latest_version"
                        label="Latest Version"
                        rules={[{ required: true, message: "Enter latest version" }]}
                    >
                        <Input placeholder="e.g. 1.0.5" />
                    </Form.Item>

                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={3} placeholder="Enter remarks" />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            style={{ background: "#0e497b", color: "white" }}
                            htmlType="submit"
                            block
                        >
                            Save Latest Version
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <Card
                title={
                    <span>
                        App Version Update&nbsp;
                        {isUnlocked
                            ? <UnlockOutlined style={{ color: "#52c41a" }} />
                            : <LockOutlined style={{ color: "#ff4d4f" }} />}
                    </span>
                }
                style={cardStyle}
            >
                {!isUnlocked && (
                    <>
                        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                            Save a <strong>Latest Update Version</strong> above to unlock this card.
                        </Text>
                        <Divider style={{ margin: "12px 0" }} />
                    </>
                )}

                <Form form={configForm} layout="vertical" onFinish={onConfigFinish}>
                    <Form.Item
                        name="latest_version"
                        label="Latest Version"
                        rules={[{ required: true, message: "Enter latest version" }]}
                    >
                        <Input disabled={!isUnlocked} />
                    </Form.Item>

                    <Form.Item
                        name="force_update_version"
                        label="Force Update Version"
                        rules={[{ required: true, message: "Enter force update version" }]}
                    >
                        <Input disabled={!isUnlocked} />
                    </Form.Item>

                    <Form.Item
                        name="update_type"
                        label="Update Type"
                        rules={[{ required: true, message: "Select update type" }]}
                    >
                        <Select placeholder="Select type" disabled={!isUnlocked}>
                            <Option value="flexible">Flexible</Option>
                            <Option value="immediate">Immediate (Force)</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="store_url"
                        label={`${platform === "ios" ? "iOS" : "Android"} Store URL`}
                        rules={[{ required: true, message: "Enter Store URL" }]}
                    >
                        <Input
                            disabled={!isUnlocked}
                            placeholder={
                                platform === "ios"
                                    ? "https://apps.apple.com/..."
                                    : "https://play.google.com/..."
                            }
                        />
                    </Form.Item>

                    <Form.Item name="version_remarks" label="Remarks">
                        <Input.TextArea rows={4} disabled={!isUnlocked} />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            style={{
                                background: isUnlocked ? "#0e497b" : "#d9d9d9",
                                color: isUnlocked ? "white" : "#999",
                                cursor: isUnlocked ? "pointer" : "not-allowed",
                            }}
                            htmlType="submit"
                            block
                            disabled={!isUnlocked}
                        >
                            Trigger Update
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}